import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Tab, Tabs } from "@mui/material";
import axios from "axios";
import MemberLifecycleTimeline from "../../components/MemberLifecycleTimeline/MemberLifecycleTimeline";
import MultipleHealthCharts from "../../components/MultipleHealthCharts/MultipleHealthCharts";
import MemberInfoChart from "../../components/Charts/MemberInfoChart";
import MemberBenefitsTable from "../../components/MemberBenefitsTable/MemberBenefitsTable";
import ActivityTab from "../../components/ActivityTab/ActivityTab";
import QuestionnaireTab from "../../components/QuestionnaireTab/QuestionnaireTab";
import HealthDataModal from "../../components/HealthDataModal/HealthDataModal";
import HealthRecordsSection from "../../components/HealthRecordsSection/HealthRecordsSection";
import RecommendBenefitModal from "../../components/RecommendBenefitModal/RecommendBenefitModal";
import styles from "./MembersPageStyles";
import { calculateAge } from "./MemberPageUitls";
import { getAvatarByGender } from "../../utils/avatarUtils";

interface Benefit {
  id: number;
  name: string;
  category: string;
  description: string;
  status: "recommended" | "assigned" | "completed";
  start_date: string;
  end_date?: string;
  benefit_type: string;
}

interface MemberDetailPageProps {
  member: any;
  goBack: () => void;
  refreshMembers: () => void;
}

const MemberDetailPage: React.FC<MemberDetailPageProps> = ({
  member,
  goBack,
  refreshMembers,
}) => {
  const [activeTab, setActiveTab] = useState("Participant Info");
  const [healthData, setHealthData] = useState([]);
  const [latestHealthData, setLatestHealthData] = useState({});
  const [isHealthDataModalOpen, setIsHealthDataModalOpen] = useState(false);
  const [isRecommendBenefitModalOpen, setIsRecommendBenefitModalOpen] =
    useState(false);
  const [currentBenefits, setCurrentBenefits] = useState<Benefit[]>([]);
  const [pastBenefits, setPastBenefits] = useState<Benefit[]>([]);
  const [recommendedBenefits, setRecommendedBenefits] = useState<Benefit[]>([]);
  const { NameAvatarBox, NameAgeBox, NameType, AgeType } = styles;
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [currentStage, setCurrentStage] = useState("new");
  const [benefitsSubTab, setBenefitsSubTab] = useState(0);

  useEffect(() => {
    fetchMemberBenefits();
    if (activeTab === "Health Info") {
      fetchHealthData();
    }
  }, [activeTab, member.id]);

  useEffect(() => {
    if (member.member_status === "approved") {
      setCurrentStage("approved");
    } else if (member.initial_assessment_completed) {
      setCurrentStage("initial_assessment");
    }
  }, [member]);

  const handleBenefitsSubTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setBenefitsSubTab(newValue);
  };

  const fetchLatestHealthData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/members/${member.id}/latest-health-data`
      );
      setLatestHealthData(response.data);
    } catch (error) {
      console.error("Error fetching latest health data:", error);
    }
  };

  const fetchHealthData = async () => {
    try {
      const [healthDataResponse, latestDataResponse] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/members/${member.id}/health-data`
        ),
        axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/members/${member.id}/latest-health-data`
        ),
      ]);
      setHealthData(healthDataResponse.data);
      setLatestHealthData(latestDataResponse.data);
    } catch (error) {
      console.error("Error fetching health data:", error);
    }
  };

  const fetchMemberBenefits = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/members/${member.id}/benefits`
      );

      const transformedBenefits = response.data.map((benefit: any) => ({
        id: benefit.id,
        name: benefit.benefit_name,
        category: benefit.benefit_type,
        description: benefit.description,
        status: benefit.status,
        start_date:
          benefit.recommendation_date ||
          benefit.assignment_date ||
          benefit.start_date,
        end_date: benefit.completion_date,
        benefit_type: benefit.benefit_type,
      }));

      setCurrentBenefits(
        transformedBenefits.filter((b: Benefit) => b.status === "assigned")
      );
      setPastBenefits(
        transformedBenefits.filter((b: Benefit) => b.status === "completed")
      );
      setRecommendedBenefits(
        transformedBenefits.filter((b: Benefit) => b.status === "recommended")
      );
    } catch (error) {
      console.error("Error fetching member benefits:", error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const handleOpenHealthDataModal = async () => {
    await fetchLatestHealthData();
    setIsHealthDataModalOpen(true);
  };

  const handleCloseHealthDataModal = () => {
    setIsHealthDataModalOpen(false);
  };

  const handleHealthDataSubmitted = () => {
    fetchHealthData();
    setIsHealthDataModalOpen(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleOpenRecommendBenefitModal = () => {
    setIsRecommendBenefitModalOpen(true);
  };

  const handleCloseRecommendBenefitModal = () => {
    setIsRecommendBenefitModalOpen(false);
  };

  const handleRecommendBenefit = async (benefitId: number | null) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/benefits/${benefitId}/recommend`,
        {
          memberId: member.id,
        }
      );
      fetchMemberBenefits();
    } catch (error) {
      console.error("Error recommending benefit:", error);
    }
  };

  const handleAssignBenefit = async (benefitId: number) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/benefits/${benefitId}/assign`,
        {
          memberId: member.id,
        }
      );
      fetchMemberBenefits();
    } catch (error) {
      console.error("Error assigning benefit:", error);
    }
  };

  const handleCompleteBenefit = async (benefitId: number) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/benefits/${benefitId}/complete`,
        {
          memberId: member.id,
        }
      );
      fetchMemberBenefits();
    } catch (error) {
      console.error("Error completing benefit:", error);
    }
  };

  if (!member) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="h6">Error: Participant data not found</Typography>
        <Button onClick={goBack}>Go Back</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Button
        onClick={goBack}
        sx={{
          color: "#474646",
          fontFamily: "Inter",
          fontSize: "20px",
          fontWeight: 600,
          letterSpacing: "1px",
        }}
      >
        Participants &gt;
      </Button>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginTop: "19px",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={getAvatarByGender(member.gender)}
            alt={`${member.gender} avatar`}
            sx={{
              width: "75.842px",
              height: "74.598px",
              borderRadius: "25.934px",
            }}
          />
          <Box sx={NameAgeBox}>
            <Typography sx={NameType} variant="body1">
              {member.codename || "Unknown Name"}
            </Typography>
            <Typography variant="body2" sx={AgeType}>
              {member.gender} {calculateAge(member.dob) || "Unknown Age"}
            </Typography>
          </Box>
        </Box>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ backgroundColor: "#F4F2FF", borderRadius: ".5cm", padding: 1 }}
        >
          <Tab label="Participant Info" value="Participant Info" />
          <Tab label="Activity" value="Activity" />
          <Tab label="Health Info" value="Health Info" />
          <Tab label="Benefits" value="Benefits" />
          <Tab label="Questionnaires" value="Questionnaires" />
        </Tabs>
      </Box>
      <Box
        sx={{
          marginTop: 4,
          padding: 3,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: 1,
        }}
      >
        {activeTab === "Participant Info" && (
          <Box>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Participant Lifecycle
            </Typography>
            <MemberLifecycleTimeline
              currentStage={currentStage}
              onActionClick={() => {}}
            />

            <Typography variant="h5" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
              Participant Health Overview
            </Typography>
            <MemberInfoChart />

            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{ mt: 4, mb: 2 }}
            ></Typography>
            <HealthRecordsSection
              memberId={member.id}
              refreshTrigger={refreshTrigger}
              setRefreshTrigger={setRefreshTrigger}
            />

            <Box sx={{ mt: 4, mb: 2 }}>
              <Typography variant="h5" fontWeight="bold">
                Participant Benefits
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Tabs
                  value={benefitsSubTab}
                  onChange={handleBenefitsSubTabChange}
                >
                  <Tab label="Current" />
                  <Tab label="Recommended" />
                  <Tab label="Past" />
                </Tabs>{" "}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenRecommendBenefitModal}
                >
                  Recommend Benefit
                </Button>
              </Box>
              <Box sx={{ mt: 2 }}>
                {benefitsSubTab === 0 &&
                  (currentBenefits.length > 0 ? (
                    <MemberBenefitsTable
                      benefits={currentBenefits}
                      pastBenefits={false}
                      onComplete={handleCompleteBenefit}
                    />
                  ) : (
                    <Typography>
                      No current benefits found for this participant.
                    </Typography>
                  ))}
                {benefitsSubTab === 1 &&
                  (recommendedBenefits.length > 0 ? (
                    <MemberBenefitsTable
                      benefits={recommendedBenefits}
                      pastBenefits={false}
                      onAssign={handleAssignBenefit}
                    />
                  ) : (
                    <Typography>
                      No recommended benefits found for this participant.
                    </Typography>
                  ))}
                {benefitsSubTab === 2 &&
                  (pastBenefits.length > 0 ? (
                    <MemberBenefitsTable
                      benefits={pastBenefits}
                      pastBenefits={true}
                    />
                  ) : (
                    <Typography>
                      No past benefits found for this participant.
                    </Typography>
                  ))}
              </Box>
            </Box>
          </Box>
        )}
        {activeTab === "Activity" && (
          <Box>
            <ActivityTab memberId={member.id} refreshMembers={refreshMembers} />
          </Box>
        )}
        {activeTab === "Health Info" && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 2,
              }}
            >
              <Typography variant="h6">Health Info</Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenHealthDataModal}
              >
                Update Health Metrics
              </Button>
            </Box>

            <MultipleHealthCharts
              healthData={healthData}
              refreshTrigger={refreshTrigger}
            />
            <Box sx={{ mt: 4 }}>
              <HealthRecordsSection
                memberId={member.id}
                refreshTrigger={refreshTrigger}
                setRefreshTrigger={setRefreshTrigger}
              />
            </Box>
            <HealthDataModal
              open={isHealthDataModalOpen}
              onClose={handleCloseHealthDataModal}
              memberId={member.id}
              latestHealthData={latestHealthData}
              onDataSubmitted={handleHealthDataSubmitted}
            />
          </Box>
        )}
        {activeTab === "Benefits" && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Tabs
                value={benefitsSubTab}
                onChange={handleBenefitsSubTabChange}
                sx={{ borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Current Benefits" />
                <Tab label="Recommended Benefits" />
                <Tab label="Past Benefits" />
              </Tabs>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenRecommendBenefitModal}
              >
                Recommend Benefit
              </Button>
            </Box>

            {benefitsSubTab === 0 && (
              <>
                {currentBenefits.length > 0 ? (
                  <MemberBenefitsTable
                    benefits={currentBenefits}
                    pastBenefits={false}
                    onComplete={handleCompleteBenefit}
                  />
                ) : (
                  <Typography>
                    No current benefits found for this participant.
                  </Typography>
                )}
              </>
            )}

            {benefitsSubTab === 1 && (
              <>
                {recommendedBenefits.length > 0 ? (
                  <MemberBenefitsTable
                    benefits={recommendedBenefits}
                    pastBenefits={false}
                    onAssign={handleAssignBenefit}
                  />
                ) : (
                  <Typography>
                    No recommended benefits found for this participant.
                  </Typography>
                )}
              </>
            )}

            {benefitsSubTab === 2 && (
              <>
                {pastBenefits.length > 0 ? (
                  <MemberBenefitsTable
                    benefits={pastBenefits}
                    pastBenefits={true}
                  />
                ) : (
                  <Typography>
                    No past benefits found for this participant.
                  </Typography>
                )}
              </>
            )}
          </Box>
        )}
        {activeTab === "Questionnaires" && (
          <Box>
            <QuestionnaireTab
              memberId={member.id}
              refreshMembers={refreshMembers}
            />
          </Box>
        )}
      </Box>

      <RecommendBenefitModal
        open={isRecommendBenefitModalOpen}
        onClose={handleCloseRecommendBenefitModal}
        onRecommend={handleRecommendBenefit}
        memberId={member.id}
      />
    </Box>
  );
};

export default MemberDetailPage;
